import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Icon,
  VStack,
  HStack,
  Badge,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaArrowRight,
  FaSeedling,
  FaChartLine,
  FaHandshake,
  FaTractor,
  FaBoxes,
  FaTruck,
  FaIndustry,
  FaGlobeAfrica,
  FaCertificate,
} from 'react-icons/fa';
import { GiClout } from 'react-icons/gi';

import Reveal from '../Components/Reveal';
import SectionHeading from '../Components/SectionHeading';
import Hero from '../Components/Hero';

import agroImage from '../Components/Assets/cafe-cacao.png';
import cacaoImage from '../Components/Assets/cacao-arabia.jpg';
import industrieImage from '../Components/Assets/indusstrie.jpeg';
import entreImage from '../Components/Assets/entre.jpg';

function Home() {
  const stats = [
    { number: '160 000', label: 'Clous produits par jour', icon: GiClout, color: 'brand.500', bg: 'brand.50' },
    { number: '2', label: 'Sites agricoles en exploitation', icon: FaSeedling, color: 'gold.600', bg: 'gold.50' },
    { number: '100%', label: 'Production locale en RDC', icon: FaCheckCircle, color: 'navy.600', bg: 'navy.50' },
    { number: '3', label: 'Sites industriels et agricoles', icon: FaGlobeAfrica, color: 'brand.600', bg: 'brand.50' },
  ];

  const avantages = [
    {
      icon: FaTractor,
      title: 'Mécanisation agricole',
      text: "Un modèle industriel prêt pour une extension rapide de nos exploitations.",
    },
    {
      icon: FaBoxes,
      title: 'Capacité industrielle',
      text: "160 000 clous par jour, soit 12,5 tonnes par mois extensible à volonté.",
    },
    {
      icon: FaCertificate,
      title: 'Certification',
      text: 'Processus préparés aux normes internationales pour l’export direct.',
    },
    {
      icon: FaHandshake,
      title: 'Partenariats locaux',
      text: 'Soutien aux coopératives et création d’emplois ruraux durables.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Culture & Récolte',
      text: "Plantations mixtes de cacaoyer et de macadamia sur plusieurs centaines d'hectares, conduites avec nos coopératives partenaires.",
      icon: FaSeedling,
      color: 'brand.500',
    },
    {
      step: '02',
      title: 'Transformation locale',
      text: 'Échelle de transformation sur place pour maximiser les marges et réduire les pertes post-récolte.',
      icon: FaIndustry,
      color: 'gold.500',
    },
    {
      step: '03',
      title: 'Production industrielle',
      text: 'Notre unité de clouterie fabrique des clous métalliques de qualité pour le BTP et la menuiserie.',
      icon: GiClout,
      color: 'navy.500',
    },
    {
      step: '04',
      title: 'Distribution & Export',
      text: 'Réseau de distribution national et capacité d’exportation de nos produits agricoles premium.',
      icon: FaTruck,
      color: 'brand.600',
    },
  ];

  const secteurs = [
    { label: 'Construction', icon: FaIndustry },
    { label: 'Menuiserie', icon: FaBoxes },
    { label: 'Quincaillerie', icon: FaHandshake },
    { label: 'Agroalimentaire', icon: FaSeedling },
    { label: 'Export', icon: FaGlobeAfrica },
  ];

  return (
    <Box>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <Hero />

      {/* ═══════════════════════ SECTEURS ═══════════════════════ */}
      <Box bg="brand.900" borderTop="1px solid" borderColor="whiteAlpha.100" overflow="hidden" py={4}>
        <HStack spacing={10} className="animate-marquee" w="max-content" pl={10}>
          {[...secteurs, ...secteurs].map((secteur, index) => (
            <HStack key={`${secteur.label}-${index}`} spacing={3} flexShrink={0}>
              <Icon as={secteur.icon} color="gold.300" boxSize={4} />
              <Text
                fontSize="sm"
                fontWeight="600"
                color="whiteAlpha.800"
                textTransform="uppercase"
                letterSpacing="0.14em"
                whiteSpace="nowrap"
              >
                {secteur.label}
              </Text>
            </HStack>
          ))}
        </HStack>
      </Box>

      {/* ═══════════════════════ CHIFFRES CLÉS ═══════════════════════ */}
      <Box bg="white" pt={{ base: 12, md: 16 }} pb={{ base: 16, md: 24 }}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
            {stats.map((stat, index) => (
              <Reveal key={stat.label} delay={index * 0.08}>
                <HStack
                  spacing={4}
                  p={6}
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="soft"
                  h="100%"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'card', borderColor: 'transparent' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="52px"
                    w="52px"
                    borderRadius="xl"
                    bg={stat.bg}
                    flexShrink={0}
                  >
                    <Icon as={stat.icon} boxSize={6} color={stat.color} />
                  </Box>
                  <Box>
                    <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800" color="gray.900" lineHeight="1.05" letterSpacing="-0.03em">
                      {stat.number}
                    </Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      {stat.label}
                    </Text>
                  </Box>
                </HStack>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ NOS PÔLES ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50" position="relative" overflow="hidden">
        <Box position="absolute" inset={0} className="bg-grid-dark" opacity={0.5} pointerEvents="none" />

        <Container maxW="7xl" position="relative">
          <SectionHeading
            eyebrow="Nos Pôles de Croissance"
            title="Deux Départements Complémentaires"
            lead="Une agriculture de rente à haute valeur ajoutée et une production industrielle de masse : deux moteurs de revenus diversifiés, pensés pour durer."
          />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {/* Agro */}
            <Reveal>
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="card"
                overflow="hidden"
                h="100%"
                display="flex"
                flexDirection="column"
                transition="all 0.4s"
                _hover={{ transform: 'translateY(-6px)', boxShadow: 'lift' }}
              >
                <Box h="230px" position="relative" overflow="hidden">
                  <Box
                    position="absolute"
                    inset={0}
                    bgImage={`url(${agroImage})`}
                    bgSize="cover"
                    bgPosition="center"
                    transition="transform 0.9s cubic-bezier(0.22,1,0.36,1)"
                    _groupHover={{ transform: 'scale(1.07)' }}
                  />
                  <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(5,42,27,0.85), rgba(5,42,27,0.15))" />
                  <HStack position="absolute" top={5} left={5} spacing={3}>
                    <Badge bg="brand.500" color="white" px={4} py={1.5} fontSize="xs" textTransform="uppercase" letterSpacing="0.08em">
                      Export & Transformation
                    </Badge>
                  </HStack>
                  <Box position="absolute" bottom={5} left={6} right={6}>
                    <HStack spacing={3}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        h="44px"
                        w="44px"
                        borderRadius="xl"
                        bg="whiteAlpha.200"
                        backdropFilter="blur(8px)"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        <Icon as={FaSeedling} boxSize={6} color="white" />
                      </Box>
                      <Heading size="lg" color="white">
                        Pôle Agrobusiness
                      </Heading>
                    </HStack>
                  </Box>
                </Box>

                <Box p={8} flex="1" display="flex" flexDirection="column">
                  <VStack align="stretch" spacing={4} flex="1">
                    {[
                      ['Actifs', "Plusieurs centaines d'hectares en exploitation"],
                      ['Stratégie', 'Plantation mixte Cacao (flux principal) / Macadamia (niche premium)'],
                      ['Objectif', 'Transition vers la transformation locale pour maximiser les marges à l’export'],
                    ].map(([label, value]) => (
                      <HStack key={label} align="start" spacing={3}>
                        <Box mt={2} w="6px" h="6px" borderRadius="full" bg="brand.400" flexShrink={0} />
                        <Text color="gray.600" fontSize="sm" lineHeight="1.7">
                          <Text as="span" fontWeight="700" color="gray.900">
                            {label} :
                          </Text>{' '}
                          {value}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  <Button
                    as={RouterLink}
                    to="/agro-business"
                    variant="link"
                    color="brand.600"
                    rightIcon={<FaArrowRight />}
                    fontWeight="700"
                    mt={6}
                    alignSelf="flex-start"
                    _hover={{ color: 'brand.800', gap: 2 }}
                  >
                    En savoir plus
                  </Button>
                </Box>
              </Box>
            </Reveal>

            {/* Industries */}
            <Reveal delay={0.1}>
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="card"
                overflow="hidden"
                h="100%"
                display="flex"
                flexDirection="column"
                transition="all 0.4s"
                _hover={{ transform: 'translateY(-6px)', boxShadow: 'lift' }}
              >
                <Box h="230px" position="relative" overflow="hidden">
                  <Box
                    position="absolute"
                    inset={0}
                    bgImage={`url(${industrieImage})`}
                    bgSize="cover"
                    bgPosition="center"
                    transition="transform 0.9s cubic-bezier(0.22,1,0.36,1)"
                  />
                  <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(8,28,52,0.9), rgba(8,28,52,0.15))" />
                  <HStack position="absolute" top={5} left={5} spacing={3}>
                    <Badge bg="navy.500" color="white" px={4} py={1.5} fontSize="xs" textTransform="uppercase" letterSpacing="0.08em">
                      Marché Intérieur
                    </Badge>
                  </HStack>
                  <Box position="absolute" bottom={5} left={6} right={6}>
                    <HStack spacing={3}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        h="44px"
                        w="44px"
                        borderRadius="xl"
                        bg="whiteAlpha.200"
                        backdropFilter="blur(8px)"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        <Icon as={GiClout} boxSize={6} color="white" />
                      </Box>
                      <Heading size="lg" color="white">
                        Pôle Industriel
                      </Heading>
                    </HStack>
                  </Box>
                </Box>

                <Box p={8} flex="1" display="flex" flexDirection="column">
                  <VStack align="stretch" spacing={4} flex="1">
                    {[
                      ['Produit', 'Clous métalliques de haute qualité pour le BTP et la menuiserie'],
                      ['Capacité', '160 000 clous/jour (12,5 tonnes/mois)'],
                      ['Avantage', 'Prix basés sur la production locale — sans coûts logistiques ni douaniers'],
                    ].map(([label, value]) => (
                      <HStack key={label} align="start" spacing={3}>
                        <Box mt={2} w="6px" h="6px" borderRadius="full" bg="navy.400" flexShrink={0} />
                        <Text color="gray.600" fontSize="sm" lineHeight="1.7">
                          <Text as="span" fontWeight="700" color="gray.900">
                            {label} :
                          </Text>{' '}
                          {value}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  <Button
                    as={RouterLink}
                    to="/industries"
                    variant="link"
                    color="navy.600"
                    rightIcon={<FaArrowRight />}
                    fontWeight="700"
                    mt={6}
                    alignSelf="flex-start"
                    _hover={{ color: 'navy.800', gap: 2 }}
                  >
                    En savoir plus
                  </Button>
                </Box>
              </Box>
            </Reveal>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ NOTRE MODÈLE ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <SectionHeading
            eyebrow="Notre Modèle Intégré"
            title="De la Terre à l'Industrie, Sans Interruption"
            lead="Une chaîne de valeur entièrement maîtrisée : la culture, la transformation, la fabrication et la distribution restent sous notre contrôle."
          />

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} position="relative">
            {process.map((item, index) => (
              <Reveal key={item.step} delay={index * 0.1}>
                <Box position="relative" h="100%">
                  {index < process.length - 1 && (
                    <Box
                      display={{ base: 'none', lg: 'block' }}
                      position="absolute"
                      top={7}
                      left="52px"
                      right="-32px"
                      h="1px"
                      bgGradient="linear(to-r, brand.200, transparent)"
                      zIndex={0}
                    />
                  )}
                  <VStack
                    align="start"
                    spacing={4}
                    p={6}
                    bg="gray.50"
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor="gray.100"
                    h="100%"
                    position="relative"
                    zIndex={1}
                    transition="all 0.35s"
                    _hover={{ bg: 'white', transform: 'translateY(-4px)', boxShadow: 'card', borderColor: 'transparent' }}
                  >
                    <HStack spacing={3} w="100%">
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        h="52px"
                        w="52px"
                        borderRadius="xl"
                        bg="white"
                        boxShadow="soft"
                        flexShrink={0}
                      >
                        <Icon as={item.icon} boxSize={6} color={item.color} />
                      </Box>
                      <Text fontSize="xs" fontWeight="800" letterSpacing="0.18em" color={item.color}>
                        {item.step}
                      </Text>
                    </HStack>
                    <Heading size="md" color="gray.900">
                      {item.title}
                    </Heading>
                    <Text color="gray.600" fontSize="sm" lineHeight="1.75">
                      {item.text}
                    </Text>
                  </VStack>
                </Box>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ ATOUTS STRATÉGIQUES ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="brand.900" color="white" position="relative" overflow="hidden">
        <Box position="absolute" inset={0} className="bg-grid" opacity={0.4} pointerEvents="none" />
        <Box
          position="absolute"
          top="-100px"
          left="-100px"
          w="360px"
          h="360px"
          borderRadius="full"
          bgGradient="radial(circle at center, rgba(20,138,85,0.35), transparent 70%)"
          pointerEvents="none"
        />

        <Container maxW="7xl" position="relative">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 12, lg: 16 }} alignItems="center">
            <GridItem>
              <SectionHeading
                align="start"
                eyebrow="Pourquoi investir"
                title="Atouts Stratégiques"
                lead="Un modèle économique ancré dans le territoire, résilient face aux importations et prêt à changer d'échelle."
                accent="gold.300"
                titleColor="white"
                leadColor="whiteAlpha.700"
                maxW="xl"
                mb={10}
              />

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={5}>
                {avantages.map((avantage, index) => (
                  <Reveal key={avantage.title} delay={index * 0.08}>
                    <HStack
                      align="start"
                      spacing={4}
                      p={5}
                      borderRadius="xl"
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                      h="100%"
                      transition="all 0.3s"
                      _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-3px)' }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        h="42px"
                        w="42px"
                        borderRadius="lg"
                        bg="whiteAlpha.200"
                        flexShrink={0}
                      >
                        <Icon as={avantage.icon} color="gold.300" />
                      </Box>
                      <Box>
                        <Text fontWeight="700" fontSize="sm" mb={1}>
                          {avantage.title}
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.700" lineHeight="1.65">
                          {avantage.text}
                        </Text>
                      </Box>
                    </HStack>
                  </Reveal>
                ))}
              </SimpleGrid>

              <Reveal delay={0.2}>
                <HStack spacing={4} pt={10} flexWrap="wrap">
                  <Button
                    as={RouterLink}
                    to="/contact"
                    size="lg"
                    bg="gold.400"
                    color="navy.900"
                    rightIcon={<FaArrowRight />}
                    _hover={{ bg: 'gold.300', transform: 'translateY(-2px)' }}
                  >
                    Devenir partenaire
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/about"
                    size="lg"
                    variant="ghostOnDark"
                    rightIcon={<FaArrowRight />}
                  >
                    En savoir plus
                  </Button>
                </HStack>
              </Reveal>
            </GridItem>

            <GridItem>
              <Reveal delay={0.15}>
                <Box position="relative">
                  <Box
                    borderRadius="2xl"
                    overflow="hidden"
                    h={{ base: '300px', md: '460px' }}
                    bgImage={`url(${entreImage})`}
                    bgSize="cover"
                    bgPosition="center"
                    boxShadow="lift"
                    position="relative"
                  >
                    <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(5,42,27,0.92), rgba(5,42,27,0.2))" />
                    <VStack position="absolute" bottom={6} left={6} right={6} align="start" spacing={2}>
                      <Badge bg="gold.400" color="navy.900" px={4} py={1.5} fontSize="xs" textTransform="uppercase" letterSpacing="0.1em">
                        Objectifs de Partenariat
                      </Badge>
                      <Heading size="lg" color="white">
                        Nous recherchons des partenaires financiers
                      </Heading>
                      <Text color="whiteAlpha.800" fontSize="sm" lineHeight="1.7">
                        Pour accélérer la mécanisation agricole, étendre la capacité de la
                        clouterie et certifier nos processus aux normes internationales.
                      </Text>
                    </VStack>
                  </Box>

                  <Box
                    position="absolute"
                    top={-6}
                    left={-4}
                    bg="white"
                    color="gray.900"
                    borderRadius="xl"
                    px={5}
                    py={4}
                    boxShadow="lift"
                    display={{ base: 'none', md: 'block' }}
                  >
                    <HStack spacing={3}>
                      <Icon as={FaChartLine} color="brand.500" boxSize={6} />
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="600" letterSpacing="0.06em">
                          DIVERSIFICATION
                        </Text>
                        <Text fontWeight="800" fontSize="sm">
                          3 flux de revenus
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </Box>
              </Reveal>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════ CTA ═══════════════════════ */}
      <Box
        color="white"
        py={{ base: 20, md: 28 }}
        position="relative"
        overflow="hidden"
        bg="navy.900"
      >
        <Box position="absolute" inset={0}>
          <Box
            position="absolute"
            inset={0}
            bgImage={`url(${cacaoImage})`}
            bgSize="cover"
            bgPosition="center"
            opacity={0.16}
          />
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(120deg, rgba(5,42,27,0.96) 0%, rgba(8,28,52,0.94) 100%)"
          />
          <Box position="absolute" inset={0} className="bg-grid" opacity={0.3} />
        </Box>

        <Container maxW="4xl" position="relative" zIndex={1} textAlign="center">
          <Reveal>
            <VStack spacing={7}>
              <Box
                bg="whiteAlpha.200"
                backdropFilter="blur(14px)"
                color="white"
                px={5}
                py={2}
                borderRadius="full"
                fontSize="xs"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.16em"
                border="1px solid"
                borderColor="whiteAlpha.300"
              >
                Opportunité d'Investissement
              </Box>

              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '4xl' }}
                fontWeight="800"
                lineHeight="1.15"
                letterSpacing="-0.03em"
              >
                Capitaliser sur l'industrialisation durable de la RDC
              </Heading>

              <Text fontSize={{ base: 'md', md: 'lg' }} color="whiteAlpha.800" maxW="2xl" lineHeight="1.8">
                Investir dans TRIMER SARL, c'est parier sur la souveraineté économique
                nationale et un modèle industriel scalable et résilient.
              </Text>

              <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  bg="white"
                  color="brand.800"
                  rightIcon={<FaArrowRight />}
                  fontWeight="700"
                  _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                >
                  Contactez-nous
                </Button>
                <Button
                  as={RouterLink}
                  to="/about"
                  size="lg"
                  variant="ghostOnDark"
                  rightIcon={<FaArrowRight />}
                  fontWeight="700"
                >
                  Découvrir TRIMER
                </Button>
              </HStack>
            </VStack>
          </Reveal>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
